import { ReduceStore } from 'flux/utils';
import Dispatcher from './Dispatcher';
import ActionTypes from './ActionTypes';
import RoleTypes from './RoleTypes';
import getNotificationSessionStorageKey
  from '../utils/getNotificationSessionStorageKey';

export const LOGIN_ERROR_REASONS = {
  NONE: 0,
  WRONG_CREDENTIALS: 1,
}

export function decodeJwtPayload(jwtToken) {
  if (
    jwtToken === null ||
    typeof jwtToken === 'undefined'
  ) {
    return null;
  }

  return JSON.parse(atob(jwtToken.split('.')[1]));
}

function isJwtTokenExpired(jwtToken) {
  const expiration = 1 * (decodeJwtPayload(jwtToken)['exp'] + '000');
  const now = new Date().getTime();

  return expiration < now;
}

export function getJWTAttribute(JWTPayload, attribute, fallback = undefined) {
  if (
    JWTPayload === null ||
    JWTPayload[attribute] === undefined
  ) {
    return fallback;
  }

  return JWTPayload[attribute];
}

class JwtStore extends ReduceStore {
  constructor() {
    super(Dispatcher);
  }

  getInitialState() {
    return {
      token: window.sessionStorage.getItem('token'),
      rawToken: window.sessionStorage.getItem('token'),
      refreshToken: window.localStorage.getItem('refresh-token'),
      didConfirmAuthentication: window.sessionStorage.getItem('did-confirm-authentication') === '1' || false,
      loginFailed: LOGIN_ERROR_REASONS.NONE
    };
  }

  getToken() {
    return this.getState().token;
  }

  didConfirmAuthentication() {
    return this.getState().didConfirmAuthentication;
  }

  getRefreshToken() {
    return this.getState().refreshToken;
  }

  getTokenDecodedPayload() {
    return decodeJwtPayload(this.getToken());
  }

  hasJWT() {
    return this.getToken() !== null;
  }

  isTokenExpired() {
    return isJwtTokenExpired(this.getState().rawToken);
  }

  getUserFirstName() {
    return getJWTAttribute(decodeJwtPayload(this.getToken()), 'firstName');
  }

  getUserLastName() {
    return getJWTAttribute(decodeJwtPayload(this.getToken()), 'lastName');
  }

  getUsername() {
    return getJWTAttribute(decodeJwtPayload(this.getToken()), 'username');
  }

  getLastLoginAt() {
    return getJWTAttribute(decodeJwtPayload(this.getToken()), 'lastLoginAt');
  }

  getHasNews(){
    return getJWTAttribute(decodeJwtPayload(this.getToken()), 'hasNews');
  }

  getBrands() {
    return getJWTAttribute(decodeJwtPayload(this.getToken()), 'brands');
  }

  getRoles() {
    return getJWTAttribute(decodeJwtPayload(this.getToken()), 'roles', []);
  }

  getMail() {
    return getJWTAttribute(decodeJwtPayload(this.getToken()), 'email');
  }

  getStores() {
    return getJWTAttribute(decodeJwtPayload(this.getToken()), 'stores',  []);
  }

  getCountries() {
    return getJWTAttribute(decodeJwtPayload(this.getToken()), 'countries', []);
  }

  getRegions() {
    return getJWTAttribute(decodeJwtPayload(this.getToken()), 'regions', []);
  }

  getAwsCredentials() {
    return getJWTAttribute(decodeJwtPayload(this.getToken()), 'aws-credentials');
  }

  hasRole(role) {
    return this.getRoles().some(loopingRole => role === loopingRole);
  }

  hasAnyRole(roles) {
    return this.getRoles().some(loopingRole => roles.indexOf(loopingRole) !== -1);
  }

  isOnlyOutletEmployee() {
    return getJWTAttribute(decodeJwtPayload(this.getToken()), 'isOnlyOutletEmployee', false);
  }

  canViewReport() {
    return this.hasAnyRole([
      'STORE_MANAGER',
      'TRAINING_MANAGER',
      'RETAIL_MANAGER',
      'AREA_MANAGER',
      'ADMIN',
      'FIELD_TRAINER',
    ]);
  }

  hasGlobalVisibility() {
    return this.hasAnyRole([
      'ADMIN',
      'TRAINING_MANAGER',
      'CRM_MANAGER',
      'RETAIL_MANAGER',
      'AREA_MANAGER',
      'FIELD_TRAINER',
      'OFFICE',
      'WHOLESALE',
      'DEFAULT'
    ]);
  }

  isStoreAssistant() {
    // Ignoring the TEST role
    const withoutTestRole = this.getRoles().filter(role => (
      role !== RoleTypes.TEST
    ));

    return ((
      // if the user has one role and it's STORE_ASSISTANT then it's OK
      withoutTestRole.length === 1 &&
      this.hasRole(RoleTypes.STORE_ASSISTANT)
    ) || (
      // if the user has two roles and they are STORE_ASSISTANT and
      // FUR_SPECIALIST then it's OK
      withoutTestRole.length === 2 &&
      this.hasRole(RoleTypes.STORE_ASSISTANT) &&
      this.hasRole(RoleTypes.FUR_SPECIALIST)
    ));
  }

  isStoreManager() {
    return this.hasRole(RoleTypes.STORE_MANAGER);
  }

  isOffice() {
    return this.hasRole(RoleTypes.OFFICE);
  }

  isAvailableFeature(feature){
    let features = getJWTAttribute(decodeJwtPayload(this.getToken()), 'features', []);
    return features.indexOf(feature) >= 0;
  }

  authenticationMethod(method = null) {
    const via = getJWTAttribute(decodeJwtPayload(this.getToken()), 'via', null);

    if (method === null) {
      return via;
    }

    return via === method;
  }

  reduce(state, action) {
    switch (action.actionType) {
      case ActionTypes.LOGIN_COMPLETED:
        window.sessionStorage.setItem('token', action.token);
        window.localStorage.setItem('refresh-token', action.refreshToken);

        window.sessionStorage.removeItem(
          getNotificationSessionStorageKey(
            getJWTAttribute(decodeJwtPayload(action.token), 'username')
          )
        );

        window.sessionStorage.setItem('did-confirm-authentication', '1');

        return {
          loginFailed: LOGIN_ERROR_REASONS.NONE,
          token: action.token,
          rawToken: action.token,
          refreshToken: action.refreshToken,
          didConfirmAuthentication: true,
        };

      case ActionTypes.CONFIRM_AUTHENTICATION:
        window.sessionStorage.setItem('did-confirm-authentication', '1');

        return {
          ...this.getState(),
          didConfirmAuthentication: true,
        }

      case ActionTypes.LOGOUT:
      case ActionTypes.ERROR_HTTP_401:
        window.sessionStorage.removeItem('token');
        window.localStorage.removeItem('refresh-token');

        window.sessionStorage.removeItem('did-confirm-authentication');

        return {
          loginFailed: LOGIN_ERROR_REASONS.NONE,
          token: null,
          rawToken: null,
          refreshToken: null,
        };

      default:
        return state;
    }
  }
}

export default new JwtStore();
